import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Briefcase } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState, DetailDrawer, formatDateTime } from "@/components/admin/ui";
import { JOB_STATUS_META, APPOINTMENT_TYPE_LABELS, CUSTOMER_CONFIRMATION_LABELS, SOURCE_LABELS } from "./officeMeta";
import { JobFormModal } from "./JobFormModal";
import { JobDetailPanel } from "./JobDetailPanel";

export const OfficeJobsTab = ({ meta, API }) => {
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ team_id: "", status: "", appointment_type: "" });
  const [creating, setCreating] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (filters.team_id) params.team_id = filters.team_id;
    if (filters.status) params.status = filters.status;
    if (filters.appointment_type) params.appointment_type = filters.appointment_type;
    axios.get(`${API}/org/office/jobs`, { params, withCredentials: true })
      .then(({ data }) => setJobs(data.jobs))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, filters]);

  useEffect(() => { load(); }, [load]);

  const canCreate = meta.scope.level !== "self";

  return (
    <>
      <Panel
        title="Jobs"
        subtitle="Every scheduled Job — created directly here or from a Sales Lead — in one canonical list."
        testId="office-jobs-tab"
        action={canCreate && (
          <button type="button" onClick={() => setCreating(true)} data-testid="office-jobs-new-btn"
            className="flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC]">
            <Plus size={14} /> New Job
          </button>
        )}
      >
        {error && <p className="mb-3 text-sm font-semibold text-red-600 dark:text-red-400" data-testid="office-jobs-error">{error}</p>}

        <div className="mb-4 flex flex-wrap gap-2" data-testid="office-jobs-filters">
          <select value={filters.team_id} onChange={(e) => setFilters((f) => ({ ...f, team_id: e.target.value }))} data-testid="office-jobs-filter-team"
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="">All Teams</option>
            {meta.teams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="office-jobs-filter-status"
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="">All Statuses</option>
            {meta.job_statuses.map((s) => <option key={s} value={s}>{JOB_STATUS_META[s].label}</option>)}
          </select>
          <select value={filters.appointment_type} onChange={(e) => setFilters((f) => ({ ...f, appointment_type: e.target.value }))} data-testid="office-jobs-filter-type"
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="">All Types</option>
            {meta.appointment_types.map((t) => <option key={t} value={t}>{APPOINTMENT_TYPE_LABELS[t]}</option>)}
          </select>
        </div>

        {!jobs ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : jobs.length === 0 ? (
          <EmptyState icon={Briefcase} title="No Jobs yet." message="Create a Job here, or schedule one from a Sales Lead — both land in this same list." testId="office-jobs-empty" />
        ) : (
          <div className="space-y-2" data-testid="office-jobs-list">
            {jobs.map((j) => (
              <button key={j.job_id} type="button" onClick={() => setSelectedJobId(j.job_id)} data-testid={`office-job-row-${j.job_id}`}
                className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 text-left transition-colors duration-150 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-900 dark:text-white">{j.title}{j.customer_name ? ` — ${j.customer_name}` : ""}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {j.team_name} · {APPOINTMENT_TYPE_LABELS[j.appointment_type]} · {formatDateTime(j.scheduled_start)} · Source: {SOURCE_LABELS[j.source]}
                  </p>
                </div>
                <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-bold ${JOB_STATUS_META[j.status].cls}`}>{JOB_STATUS_META[j.status].label}</span>
              </button>
            ))}
          </div>
        )}
      </Panel>

      {creating && (
        <JobFormModal meta={meta} API={API} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
      )}

      <DetailDrawer open={!!selectedJobId} onClose={() => setSelectedJobId(null)} title="Job" testId="office-job-detail-drawer">
        {selectedJobId && (
          <JobDetailPanel
            jobId={selectedJobId}
            meta={meta}
            API={API}
            onChanged={load}
            statusMeta={JOB_STATUS_META}
            confirmationLabels={CUSTOMER_CONFIRMATION_LABELS}
            appointmentTypeLabels={APPOINTMENT_TYPE_LABELS}
          />
        )}
      </DetailDrawer>
    </>
  );
};
